import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import unifiedApi from '../../utils/unifiedApi';

// Styled components for better soccer table styling
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
}));

const StyledTableHead = styled(TableHead)(() => ({
  backgroundColor: '#2E4156 !important', // Quail Game primary blue
  '& .MuiTableCell-root': {
    backgroundColor: '#2E4156 !important',
    color: '#ffffff !important',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    borderBottom: '2px solid #ffffff',
  },
}));

const PositionCell = styled(TableCell)(() => ({
  fontWeight: 'bold',
  fontSize: '1rem',
  textAlign: 'center',
  width: '40px',
}));

const TeamCell = styled(TableCell)(() => ({
  fontWeight: 'bold',
  textAlign: 'left',
  minWidth: '150px',
}));

const FormCell = styled(TableCell)(() => ({
  textAlign: 'center',
  padding: '4px',
  width: '30px',
}));

// Styled components for mobile view
const MobileStandingCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  border: '1px solid rgba(0, 0, 0, 0.12)',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const PositionBadge = styled(Box)(({ position }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: position <= 3 ? (
    position === 1 ? '#FFD700' :
    position === 2 ? '#C0C0C0' :
    '#CD7F32'
  ) : '#2E4156',
  color: position <= 3 ? '#000' : '#fff',
  fontWeight: 'bold',
  fontSize: '1rem',
  marginRight: 8,
}));

// Component to render individual form indicators (W/D/L)
const FormIndicator = ({ result }) => {
  const getColor = (result) => {
    switch (result.toUpperCase()) {
      case 'W': return '#4caf50'; // success green
      case 'D': return '#ff9800'; // warning orange
      case 'L': return '#f44336'; // error red
      default: return '#9e9e9e'; // default grey
    }
  };

  const getLabel = (result) => {
    switch (result.toUpperCase()) {
      case 'W': return 'W';
      case 'D': return 'D';
      case 'L': return 'L';
      default: return '?';
    }
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: '12px',
        backgroundColor: getColor(result),
        color: 'white',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        margin: '1px'
      }}
    >
      {getLabel(result)}
    </Box>
  );
};

// Mobile-friendly standings component
const MobileStandings = ({ standings, isPublic, orgId, leagueId }) => {
  const getDisplayColor = (jerseyColorHex) => {
    if (!jerseyColorHex) return 'inherit';
    const isWhiteColor = (color) => {
      if (!color) return false;
      const normalizedColor = color.toLowerCase().trim();
      return normalizedColor === '#ffffff' || 
             normalizedColor === '#fff' || 
             normalizedColor === 'white' || 
             normalizedColor === 'rgb(255,255,255)' ||
             normalizedColor === 'rgb(255, 255, 255)';
    };
    return isWhiteColor(jerseyColorHex) ? '#666666' : jerseyColorHex;
  };

  const renderMobileForm = (recentForm) => {
    if (!recentForm || recentForm.length === 0) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {Array(5).fill('-').map((_, index) => (
            <Box key={index} sx={{ 
              width: 20, height: 20, display: 'flex', 
              alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', color: 'text.disabled'
            }}>
              -
            </Box>
          ))}
        </Box>
      );
    }

    let form = recentForm.length >= 5 
      ? recentForm.slice(-5) 
      : recentForm.padStart(5, '-');
    
    const formArray = form.split('').reverse();

    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {formArray.map((result, index) => (
          <Box key={index}>
            {result === '-' ? (
              <Box sx={{ 
                width: 20, height: 20, display: 'flex', 
                alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', color: 'text.disabled'
              }}>
                -
              </Box>
            ) : (
              <FormIndicator result={result} />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {standings.standings.map((team) => (
        <MobileStandingCard key={team.teamId}>
          <CardContent sx={{ padding: 2, '&:last-child': { paddingBottom: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PositionBadge position={team.position}>
                  {team.position}
                </PositionBadge>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {team.logoUrl && (
                      <img 
                        src={team.logoUrl} 
                        alt={`${team.teamName} logo`}
                        style={{
                          width: '20px',
                          height: '20px',
                          objectFit: 'contain',
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {team.jerseyColorHex && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: getDisplayColor(team.jerseyColorHex),
                          borderRadius: '50%',
                          border: '1px solid rgba(0,0,0,0.2)',
                          flexShrink: 0
                        }}
                        title={team.jerseyColorName || 'Team color'}
                      />
                    )}
                    {isPublic ? (
                      <Link 
                        to={`/teams/${team.teamSlug}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 'bold',
                          fontSize: '1rem'
                        }}
                      >
                        {team.teamName}
                      </Link>
                    ) : (
                      <Link 
                        to={`/organization/${orgId}/league/${leagueId}/team/${team.teamId}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 'bold',
                          fontSize: '1rem'
                        }}
                      >
                        {team.teamName}
                      </Link>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Manager: {team.managerName || 'No manager'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', lineHeight: 1 }}>
                  {team.points}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  pts
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                  {team.matchesPlayed}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MP
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', lineHeight: 1 }}>
                  {team.wins}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  W
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main', lineHeight: 1 }}>
                  {team.draws}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  D
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main', lineHeight: 1 }}>
                  {team.losses}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  L
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                  {team.goalsFor}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  GF
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                  {team.goalsAgainst}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  GA
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold',
                  color: team.goalDifference > 0 ? 'success.main' : 
                         team.goalDifference < 0 ? 'error.main' : 'text.primary',
                  lineHeight: 1
                }}>
                  {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  GD
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              {renderMobileForm(team.recentForm)}
            </Box>
          </CardContent>
        </MobileStandingCard>
      ))}
    </Box>
  );
};

const StandingsComponent = ({ leagueId, orgId, standingsData = null, isPublic = false, hideTitle = false }) => {
  const [standings, setStandings] = useState(standingsData);
  const [loading, setLoading] = useState(!standingsData);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isWhiteColor = (color) => {
    if (!color) return false;
    const normalizedColor = color.toLowerCase().trim();
    // Check for common white color representations
    return normalizedColor === '#ffffff' || 
           normalizedColor === '#fff' || 
           normalizedColor === 'white' || 
           normalizedColor === 'rgb(255,255,255)' ||
           normalizedColor === 'rgb(255, 255, 255)';
  };

  const getDisplayColor = (jerseyColorHex) => {
    if (!jerseyColorHex) return 'inherit';
    return isWhiteColor(jerseyColorHex) ? '#666666' : jerseyColorHex;
  };

  useEffect(() => {
    // If standingsData is provided, don't fetch from API
    if (standingsData) {
      setStandings(standingsData);
      setLoading(false);
      return;
    }

    const fetchStandings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await unifiedApi.getStandingsByLeagueId(leagueId);
        setStandings(data);
      } catch (err) {
        console.error('Error fetching standings:', err);
        setError(`Failed to load standings: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (leagueId) {
      fetchStandings();
    }
  }, [leagueId, standingsData]);

  const renderFormCells = (recentForm) => {
    if (!recentForm || recentForm.length === 0) {
      return Array(5).fill(null).map((_, index) => (
        <FormCell key={index}>-</FormCell>
      ));
    }

    // Take the last 5 games and reverse to show most recent first (left to right)
    let form = recentForm.length >= 5 
      ? recentForm.slice(-5) 
      : recentForm.padStart(5, '-');
    
    // Split, reverse, and rejoin to show most recent games on the left
    const formArray = form.split('').reverse();

    return formArray.map((result, index) => (
      <FormCell key={index}>
        {result === '-' ? '-' : <FormIndicator result={result} />}
      </FormCell>
    ));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading standings...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!standings || !standings.standings || standings.standings.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No standings data available for this league.
      </Alert>
    );
  }

  return (
    <Box>
      {!hideTitle && (
        <>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {standings.leagueName} - League Table
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Last updated: {new Date(standings.lastUpdated).toLocaleString()}
          </Typography>
        </>
      )}

      {isMobile ? (
        <MobileStandings 
          standings={standings} 
          isPublic={isPublic} 
          orgId={orgId} 
          leagueId={leagueId}
        />
      ) : (
      <StyledTableContainer component={Paper}>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '40px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>#</TableCell>
              <TableCell align="left" sx={{ minWidth: '150px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>Team</TableCell>
              <TableCell align="left" sx={{ minWidth: '120px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>Manager</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>MP</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>W</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>D</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>L</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>GF</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>GA</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>GD</TableCell>
              <TableCell align="center" sx={{ width: '50px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>Pts</TableCell>
              <TableCell align="center" sx={{ width: '30px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>5</TableCell>
              <TableCell align="center" sx={{ width: '30px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>4</TableCell>
              <TableCell align="center" sx={{ width: '30px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>3</TableCell>
              <TableCell align="center" sx={{ width: '30px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>2</TableCell>
              <TableCell align="center" sx={{ width: '30px', backgroundColor: '#2E4156 !important', color: 'white !important' }}>1</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {standings.standings.map((team) => (
              <TableRow 
                key={team.teamId} 
                hover
                sx={{ 
                  '&:nth-of-type(odd)': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.02)' 
                  },
                  // Highlight top 3 positions with subtle colors
                  ...(team.position === 1 && { 
                    backgroundColor: 'rgba(255, 215, 0, 0.1)' // Gold tint for 1st
                  }),
                  ...(team.position === 2 && { 
                    backgroundColor: 'rgba(192, 192, 192, 0.1)' // Silver tint for 2nd
                  }),
                  ...(team.position === 3 && { 
                    backgroundColor: 'rgba(205, 127, 50, 0.1)' // Bronze tint for 3rd
                  }),
                }}
              >
                <PositionCell>{team.position}</PositionCell>
                <TeamCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {team.logoUrl && (
                      <img 
                        src={team.logoUrl} 
                        alt={`${team.teamName} logo`}
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain',
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {team.jerseyColorHex && (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: getDisplayColor(team.jerseyColorHex),
                          borderRadius: '50%',
                          border: '1px solid rgba(0,0,0,0.2)',
                          flexShrink: 0
                        }}
                        title={team.jerseyColorName || 'Team color'}
                      />
                    )}
                    {isPublic ? (
                      <Link 
                        to={`/teams/${team.teamSlug}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 'bold'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {team.teamName}
                      </Link>
                    ) : (
                      <Link 
                        to={`/organization/${orgId}/league/${leagueId}/team/${team.teamId}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 'bold'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {team.teamName}
                      </Link>
                    )}
                  </Box>
                </TeamCell>
                <TableCell align="left" sx={{ color: 'text.secondary' }}>
                  {team.managerName || 'No manager'}
                </TableCell>
                <TableCell align="center">{team.matchesPlayed}</TableCell>
                <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  {team.wins}
                </TableCell>
                <TableCell align="center" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                  {team.draws}
                </TableCell>
                <TableCell align="center" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  {team.losses}
                </TableCell>
                <TableCell align="center">{team.goalsFor}</TableCell>
                <TableCell align="center">{team.goalsAgainst}</TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: team.goalDifference > 0 ? 'success.main' : 
                           team.goalDifference < 0 ? 'error.main' : 'text.primary'
                  }}
                >
                  {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    color: 'primary.main'
                  }}
                >
                  {team.points}
                </TableCell>
                {renderFormCells(team.recentForm)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      )}

      {!isMobile && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Form Guide:
          </Typography>
          <FormIndicator result="W" />
          <Typography variant="caption">Win</Typography>
          <FormIndicator result="D" />
          <Typography variant="caption">Draw</Typography>
          <FormIndicator result="L" />
          <Typography variant="caption">Loss</Typography>
        </Box>
      )}
    </Box>
  );
};

export default StandingsComponent;